import Plyr from 'plyr/dist/plyr.js';
import 'plyr/dist/plyr.css';

/**
 * VideoService - Singleton service for managing Plyr video playback
 * Similar to AudioService but wraps Plyr's API for YouTube video embedding
 */
class VideoService {
  constructor() {
    this.player = null;
    this.isPlaying = false;
    this.currentVideoId = null;
    this.videoEndedCallback = null;
    this.progressUpdateCallback = null;
    this.fullscreenChangeCallback = null;
    this.playingStateChangeCallback = null;
    this.autoplayBlockedCallback = null;
    this.progressInterval = null;
    this.documentFullscreenHandler = null;
    this._isYouTubeReady = false;
    this._hasPlayedBefore = false;
    this._isIOS = typeof navigator !== 'undefined' && (
      /(iPad|iPhone|iPod)/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
    this._wasFullscreen = false;
    this._resizeHandler = null;
    this._playbackVerifyTimer = null;
  }

  /**
   * Initialize Plyr player instance
   * @param {HTMLElement} containerElement - The container element for the player
   * @param {Object} options - Plyr configuration options
   */
  initialize(containerElement, options = {}) {
    if (this.player) {
      this.destroy();
    }

    const defaultOptions = {
      controls: [
        'play-large',
        'play',
        'progress',
        'current-time',
        'duration',
        'mute',
        'volume',
        'settings',
        'pip',
        'fullscreen',
      ],
      settings: ['quality'],
      quality: {
        default: 720,
        options: [4320, 2880, 2160, 1440, 1080, 720, 576, 480, 360, 240],
        forced: true,
        onChange: () => {},
      },
      youtube: {
        noCookie: false,
        rel: 0,
        showinfo: 0,
        iv_load_policy: 3,
        modestbranding: 1,
        playsinline: 1,
      },
      ratio: '16:9',
    };

    this.player = new Plyr(containerElement, {
      ...defaultOptions,
      ...options,
    });

    // Set up event listeners
    this.player.on('play', () => {
      this.isPlaying = true;
      this._hasPlayedBefore = true;
      this.startProgressMonitoring();
      if (this.playingStateChangeCallback) {
        this.playingStateChangeCallback(true);
      }

      // Automatically enter fullscreen when video starts playing
      // Small delay to ensure player is ready
      setTimeout(() => {
        this.enterFullscreen();
      }, 500);
    });

    this.player.on('pause', () => {
      this.isPlaying = false;
      this.stopProgressMonitoring();
      if (this.playingStateChangeCallback) {
        this.playingStateChangeCallback(false);
      }
    });

    this.player.on('ended', () => {
      this.isPlaying = false;
      this.stopProgressMonitoring();
      if (this.playingStateChangeCallback) {
        this.playingStateChangeCallback(false);
      }
      if (this.videoEndedCallback) {
        this.videoEndedCallback();
      }
    });

    this.player.on('enterfullscreen', () => {
      if (this.fullscreenChangeCallback) {
        this.fullscreenChangeCallback(true);
      }
    });

    this.player.on('exitfullscreen', () => {
      if (this.fullscreenChangeCallback) {
        this.fullscreenChangeCallback(false);
      }
    });

    // Add document-level fullscreen listeners for iOS Safari compatibility
    this.setupDocumentFullscreenListeners();

    // Add resize-based fullscreen exit detection (iOS Safari fallback)
    this.setupResizeFullscreenDetection();

    // Track when YouTube embed is ready (fires after each source change)
    this.player.on('ready', () => {
      this._isYouTubeReady = true;

      // Attach onAutoplayBlocked listener to the YouTube embed
      const youtubeEmbed = this.player.embed;
      if (youtubeEmbed && typeof youtubeEmbed.addEventListener === 'function') {
        youtubeEmbed.addEventListener('onAutoplayBlocked', () => {
          this.isPlaying = false;
          if (this.playingStateChangeCallback) {
            this.playingStateChangeCallback(false);
          }
          if (this.autoplayBlockedCallback) {
            this.autoplayBlockedCallback();
          }
        });
      }
    });

    return this.player;
  }

  /**
   * Load a YouTube video by ID
   * @param {string} videoId - YouTube video ID
   * @param {boolean} autoplay - Whether to autoplay when ready (default: false)
   */
  loadVideo(videoId, autoplay = false) {
    if (!this.player) {
      console.error('Player not initialized');
      return;
    }

    this.currentVideoId = videoId;

    // When the YouTube embed is already initialized, autoplay is requested, and
    // playback has occurred before, use YouTube's native loadVideoById() API.
    // This reuses the existing iframe (preserving iOS Safari's user activation
    // state) instead of Plyr's source setter which destroys and recreates it.
    // We require _hasPlayedBefore because loadVideoById bypasses Plyr's UI
    // (poster, controls sync) and on iOS the first play needs a real gesture
    // on Plyr's play button to establish user activation inside the iframe.
    const youtubeEmbed = this.player.embed;
    const canUseNativeApi = this._isYouTubeReady &&
      this._hasPlayedBefore &&
      youtubeEmbed &&
      typeof youtubeEmbed.loadVideoById === 'function';

    if (canUseNativeApi && autoplay) {
      try {
        // Verify the embed is functional by checking player state
        const state = youtubeEmbed.getPlayerState();
        if (state !== undefined && state !== null) {
          youtubeEmbed.loadVideoById(videoId);
          // Verify playback actually started — on iOS Safari, loadVideoById()
          // can sometimes fail silently even with sticky user activation
          this._verifyPlayback(youtubeEmbed);
          return;
        }
      } catch (e) {
        // YouTube native API unavailable, fall through to Plyr
      }
    }

    // Fallback: use Plyr's source setter (destroys and recreates iframe)
    this._isYouTubeReady = false;

    // On iOS, skip autoplay for the first video — the play() call will be blocked
    // because no user gesture has occurred inside the YouTube iframe yet.
    // The VideoTapToPlayOverlay will handle the first play via a real user tap.
    if (autoplay && !(this._isIOS && !this._hasPlayedBefore)) {
      const playOnReady = () => {
        this.player.play();
        this.player.off('ready', playOnReady);
      };
      this.player.on('ready', playOnReady);
    }

    this.player.source = {
      type: 'video',
      sources: [
        {
          src: videoId,
          provider: 'youtube',
        },
      ],
    };
  }

  /**
   * Verify that playback started after loadVideoById().
   * Polls the YouTube player state and retries playVideo() if stuck.
   * @param {Object} youtubeEmbed - The YouTube iframe API embed object
   */
  _verifyPlayback(youtubeEmbed) {
    // Clear any existing verification timer
    if (this._playbackVerifyTimer) {
      clearTimeout(this._playbackVerifyTimer);
    }

    let checks = 0;
    const maxChecks = 4;
    const checkDelay = 600;

    const verify = () => {
      checks++;
      if (!this.player || !youtubeEmbed) return;

      try {
        const state = youtubeEmbed.getPlayerState();
        // YouTube player states: -1 unstarted, 0 ended, 1 playing, 2 paused, 3 buffering, 5 cued
        if (state === 1 || state === 3) {
          // Playing or buffering — playback is proceeding
          return;
        }

        if (checks < maxChecks) {
          this._playbackVerifyTimer = setTimeout(verify, checkDelay);
        } else {
          // Playback still hasn't started after all checks — force play
          if (typeof youtubeEmbed.playVideo === 'function') {
            youtubeEmbed.playVideo();
          }
        }
      } catch (e) {
        // Embed became unavailable, stop checking
      }
    };

    // Start first check after a short delay to let loadVideoById settle
    this._playbackVerifyTimer = setTimeout(verify, checkDelay);
  }

  /**
   * Play the current video
   */
  play() {
    if (this.player) {
      // Unmute in case it was muted by fadeOutAndPause
      if (this.player.muted) {
        this.player.muted = false;
      }
      // Also try YouTube native unmute
      const youtubeEmbed = this.player.embed;
      if (youtubeEmbed && typeof youtubeEmbed.unMute === 'function') {
        youtubeEmbed.unMute();
      }
      this.player.play();
    }
  }

  /**
   * Pause the current video
   */
  pause() {
    if (this.player) {
      this.player.pause();
    }
  }

  /**
   * Fade out video audio and then pause
   * Uses YouTube's native API via player.embed for more reliable volume control
   * (Plyr's volume property goes through an abstraction layer that can cause latency)
   * @param {number} duration - Fade duration in milliseconds (default 200ms)
   * @returns {Promise} - Resolves when fade and pause are complete
   */
  fadeOutAndPause(duration = 200) {
    return new Promise((resolve) => {
      if (!this.player) {
        resolve();
        return;
      }

      // Try to access YouTube embed directly for more reliable volume control
      const youtubeEmbed = this.player.embed;
      const useNativeApi = youtubeEmbed && typeof youtubeEmbed.setVolume === 'function';

      // Get current volume (YouTube uses 0-100, Plyr uses 0-1)
      let startVolume;
      if (useNativeApi) {
        startVolume = youtubeEmbed.getVolume ? youtubeEmbed.getVolume() : 100;
      } else {
        startVolume = this.player.volume * 100; // Convert Plyr's 0-1 to 0-100
      }

      // If already muted or volume is 0, just pause
      if (startVolume === 0 || this.player.muted) {
        this.player.pause();
        this.isPlaying = false;
        resolve();
        return;
      }

      // Use fewer steps for YouTube iframe API compatibility
      // Each API call goes through postMessage - too many causes glitches
      const steps = 6;
      const stepDuration = duration / steps;
      const volumeStep = startVolume / steps;
      let currentStep = 0;

      const fadeInterval = setInterval(() => {
        currentStep++;

        if (!this.player) {
          clearInterval(fadeInterval);
          resolve();
          return;
        }

        const newVolume = Math.max(0, Math.round(startVolume - (volumeStep * currentStep)));

        if (currentStep >= steps) {
          clearInterval(fadeInterval);

          // Mute and set volume to 0
          if (useNativeApi) {
            youtubeEmbed.setVolume(0);
            if (youtubeEmbed.mute) youtubeEmbed.mute();
          } else {
            this.player.volume = 0;
            this.player.muted = true;
          }

          // Delay before stop to ensure mute is applied
          setTimeout(() => {
            if (this.player) {
              // Use YouTube's stopVideo if available - it fully stops rather than just pausing
              // This is cleaner and less likely to cause artifacts on destroy
              if (useNativeApi && typeof youtubeEmbed.stopVideo === 'function') {
                youtubeEmbed.stopVideo();
              } else {
                this.player.pause();
              }
              this.isPlaying = false;

              // Restore volume after stop, while still muted (will unmute on next play)
              setTimeout(() => {
                if (this.player) {
                  if (useNativeApi && youtubeEmbed.setVolume) {
                    youtubeEmbed.setVolume(startVolume);
                  } else {
                    this.player.volume = startVolume / 100;
                  }
                }
                resolve();
              }, 50);
            } else {
              resolve();
            }
          }, 30);
        } else {
          // Gradual volume reduction
          if (useNativeApi) {
            youtubeEmbed.setVolume(newVolume);
          } else {
            this.player.volume = newVolume / 100;
          }
        }
      }, stepDuration);
    });
  }

  /**
   * Toggle play/pause
   */
  togglePlayPause() {
    if (this.player) {
      this.player.togglePlay();
    }
  }

  /**
   * Enter fullscreen mode
   */
  enterFullscreen() {
    if (this.player && this.player.fullscreen) {
      this.player.fullscreen.enter();
    }
  }

  /**
   * Exit fullscreen mode
   */
  exitFullscreen() {
    if (this.player && this.player.fullscreen) {
      this.player.fullscreen.exit();
    }
  }

  /**
   * Check if currently in fullscreen
   * @returns {boolean}
   */
  isFullscreen() {
    if (this.player && this.player.fullscreen) {
      return this.player.fullscreen.active;
    }
    return false;
  }

  /**
   * Set up document-level fullscreen listeners for iOS Safari compatibility
   * iOS Safari uses webkit-specific events that Plyr may not catch
   */
  setupDocumentFullscreenListeners() {
    // Remove existing listener if any
    this.removeDocumentFullscreenListeners();

    // Create handler that checks fullscreen state
    this.documentFullscreenHandler = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.webkitCurrentFullScreenElement
      );
      if (this.fullscreenChangeCallback) {
        this.fullscreenChangeCallback(isFullscreen);
      }
    };

    // Standard fullscreen events
    document.addEventListener('fullscreenchange', this.documentFullscreenHandler);
    // Webkit/Safari fullscreen events
    document.addEventListener('webkitfullscreenchange', this.documentFullscreenHandler);
  }

  /**
   * Set up a resize listener to detect fullscreen exit on iOS Safari,
   * where Plyr/document fullscreen events may not fire reliably for YouTube iframes
   */
  setupResizeFullscreenDetection() {
    this.removeResizeFullscreenDetection();

    this._resizeHandler = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.webkitCurrentFullScreenElement
      );
      if (this._wasFullscreen && !isFullscreen) {
        if (this.fullscreenChangeCallback) {
          this.fullscreenChangeCallback(false);
        }
      }
      this._wasFullscreen = isFullscreen;
    };

    window.addEventListener('resize', this._resizeHandler);
  }

  /**
   * Remove resize-based fullscreen detection listener
   */
  removeResizeFullscreenDetection() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }
  }

  /**
   * Remove document-level fullscreen listeners
   */
  removeDocumentFullscreenListeners() {
    if (this.documentFullscreenHandler) {
      document.removeEventListener('fullscreenchange', this.documentFullscreenHandler);
      document.removeEventListener('webkitfullscreenchange', this.documentFullscreenHandler);
      this.documentFullscreenHandler = null;
    }
  }

  /**
   * Seek to a specific position (percentage)
   * @param {number} positionPercent - Position as percentage (0-100)
   */
  seek(positionPercent) {
    if (this.player && this.player.duration) {
      const timeInSeconds = (positionPercent / 100) * this.player.duration;
      this.player.currentTime = timeInSeconds;
    }
  }

  /**
   * Get current playback progress information
   * @returns {Object} Progress data
   */
  getVideoProgress() {
    if (!this.player) {
      return {
        currentTime: 0,
        duration: 0,
        percent: 0,
        currentTimeFormatted: '00:00',
        durationFormatted: '00:00',
      };
    }

    const currentTime = this.player.currentTime || 0;
    const duration = this.player.duration || 0;
    const percent = duration > 0 ? (currentTime / duration) * 100 : 0;

    return {
      currentTime,
      duration,
      percent,
      currentTimeFormatted: this.formatTime(currentTime),
      durationFormatted: this.formatTime(duration),
    };
  }

  /**
   * Format time in seconds to MM:SS format
   * @param {number} timeInSeconds - Time in seconds
   * @returns {string} Formatted time string
   */
  formatTime(timeInSeconds) {
    if (!timeInSeconds || isNaN(timeInSeconds)) {
      return '00:00';
    }

    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Set callback for when video ends
   * @param {Function} callback - Callback function
   */
  setVideoEndedCallback(callback) {
    this.videoEndedCallback = callback;
  }

  /**
   * Set callback for progress updates
   * @param {Function} callback - Callback function
   */
  setProgressUpdateCallback(callback) {
    this.progressUpdateCallback = callback;
  }

  /**
   * Set callback for fullscreen changes
   * @param {Function} callback - Callback function that receives boolean (true for fullscreen, false for exit)
   */
  setFullscreenChangeCallback(callback) {
    this.fullscreenChangeCallback = callback;
  }

  /**
   * Set callback for video playing state changes
   * @param {Function} callback - Callback function that receives boolean (true for playing, false for paused/ended)
   */
  setPlayingStateChangeCallback(callback) {
    this.playingStateChangeCallback = callback;
  }

  /**
   * Set callback for when autoplay is blocked (e.g., iOS Safari)
   * @param {Function} callback - Callback function
   */
  setAutoplayBlockedCallback(callback) {
    this.autoplayBlockedCallback = callback;
  }

  /**
   * Start monitoring playback progress
   */
  startProgressMonitoring() {
    this.stopProgressMonitoring();
    this.progressInterval = setInterval(() => {
      if (this.progressUpdateCallback && this.player) {
        this.progressUpdateCallback(this.getVideoProgress());
      }
    }, 200);
  }

  /**
   * Stop monitoring playback progress
   */
  stopProgressMonitoring() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  /**
   * Get current player instance
   * @returns {Plyr} Plyr instance
   */
  getPlayer() {
    return this.player;
  }

  /**
   * Check if video is currently playing
   * @returns {boolean}
   */
  getIsPlaying() {
    return this.isPlaying;
  }

  /**
   * Destroy the player instance and clean up
   */
  destroy() {
    this.stopProgressMonitoring();
    this.removeDocumentFullscreenListeners();
    this.removeResizeFullscreenDetection();
    if (this._playbackVerifyTimer) {
      clearTimeout(this._playbackVerifyTimer);
      this._playbackVerifyTimer = null;
    }
    if (this.player) {
      // Ensure video is stopped and muted before destroying to prevent audio artifacts
      try {
        const youtubeEmbed = this.player.embed;
        if (youtubeEmbed) {
          if (typeof youtubeEmbed.mute === 'function') youtubeEmbed.mute();
          if (typeof youtubeEmbed.stopVideo === 'function') youtubeEmbed.stopVideo();
        } else {
          this.player.muted = true;
          this.player.pause();
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
      this.player.destroy();
      this.player = null;
    }
    this.isPlaying = false;
    this.currentVideoId = null;
    this._isYouTubeReady = false;
    this._hasPlayedBefore = false;
    this._wasFullscreen = false;
  }
}

export default new VideoService();
